import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  useTheme,
  Grid,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Autocomplete,
  Stack,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFormik } from "formik";
import { FundName } from "./Schema";
import { useSelector } from "react-redux";
import { findCustByName } from "../../component/Methods";
import { selectCustodians, selectManagers } from "../../Redux/Reducers/selectors";

const fontFamily = `'Inter', 'SF Pro Display', 'Roboto', 'Arial', sans-serif`;

const FundsDialog = ({ open, onClose, onSave, fund }) => {
  const theme = useTheme();
  const custodians = useSelector(selectCustodians);
  const managers = useSelector(selectManagers);
  const isEdit = !!fund;

  const [accounts, setAccounts] = useState([
    { custodian: "", accountNumber: "", accountName: "" },
  ]);

  const initialValues = {
    name: fund?.name || "",
    abbreviation: fund?.abbreviation || "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: FundName,
    enableReinitialize: true,
    onSubmit: (values) => {
      const accountsData = {};
      const managerId = managers[0]?.managerId;

      if (Array.isArray(accounts)) {
        accounts.forEach((account) => {
          if (
            account.accountName &&
            account.custodian &&
            account.accountNumber
          ) {
            accountsData[account.accountName] = {
              custodian: findCustByName(account.custodian, custodians),
              number: account.accountNumber,
            };
          }
        });
      }

      const fundData = {
        abbreviation: values.abbreviation,
        accounts: accountsData,
        managerId: managerId,
        name: values.name,
      };
      onSave(fundData);
    },
  });

  // Initialize accounts for edit mode
  useEffect(() => {
    if (isEdit && fund?.fundAccounts) {
      const existingAccounts = fund.fundAccounts.map((account) => ({
        custodian: custodians?.find(c => c.custodianId === account.custodian)?.name || "",
        accountNumber: account.number || "",
        accountName: account.name || "",
      }));
      setAccounts(existingAccounts.length > 0 ? existingAccounts : [
        { custodian: "", accountNumber: "", accountName: "" }
      ]);
    } else {
      setAccounts([{ custodian: "", accountNumber: "", accountName: "" }]);
    }
  }, [fund, custodians, isEdit]);

  const handleClose = () => {
    formik.resetForm();
    setAccounts([{ custodian: "", accountNumber: "", accountName: "" }]);
    onClose();
  };

  const handleAddAccount = () => {
    setAccounts((prev) => [
      ...prev,
      { custodian: "", accountNumber: "", accountName: "" },
    ]);
  };

  const handleRemoveAccount = (index) => {
    if (accounts.length > 1) {
      setAccounts((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleAccountChange = (index, field, value) => {
    setAccounts((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 4px 24px 0 rgba(16,30,54,0.10)',
          fontFamily,
          p: 0,
        },
      }}
    >
      <Box sx={{ px: 4, pt: 3, pb: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
              <AccountBalanceWalletIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: 22, fontFamily, lineHeight: 1.2 }}
              >
                {isEdit ? "Edit Fund" : "Add Fund"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, fontFamily, mt: 0.5 }}
              >
                {isEdit ? "Update fund details below." : "Enter fund details to add a new fund."}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 2, pb: 2, px: 4 }}>
        <Stack spacing={3}>
          <Grid container spacing={1} justifyContent="space-between">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1,
                    fontFamily
                  }}
                >
                  Full Name
                </Typography>
                <TextField
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  fullWidth
                  placeholder="Enter fund full name"
                  inputProps={{ style: { fontFamily } }}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1,
                    fontFamily
                  }}
                >
                  Abbreviation
                </Typography>
                <TextField
                  name="abbreviation"
                  disabled={isEdit}
                  value={formik.values.abbreviation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.abbreviation && Boolean(formik.errors.abbreviation)}
                  helperText={formik.touched.abbreviation && formik.errors.abbreviation}
                  fullWidth
                  placeholder="Enter fund abbreviation"
                  inputProps={{ style: { fontFamily } }}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>

          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, pl: 1 }}>
                Accounts
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddAccount}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                }}
              >
                Add Account
              </Button>
            </Box>

            <Table>
              <TableBody>
                {accounts.map((account, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ border: "none", p: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, md: 3 }}>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                mb: 1,
                                fontFamily
                              }}
                            >
                              Custodian
                            </Typography>
                            <Autocomplete
                              value={account.custodian}
                              onChange={(e, newValue) =>
                                handleAccountChange(index, "custodian", newValue || "")
                              }
                              options={custodians?.map((c) => c?.name) || []}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  placeholder="Select custodian"
                                  inputProps={{ ...params.inputProps, style: { fontFamily } }}
                                  variant="outlined"
                                />
                              )}
                            />
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                mb: 1,
                                fontFamily
                              }}
                            >
                              Account Number
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Enter account number"
                              value={account.accountNumber}
                              onChange={(e) =>
                                handleAccountChange(index, "accountNumber", e.target.value)
                              }
                              inputProps={{ style: { fontFamily } }}
                              variant="outlined"
                            />
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                mb: 1,
                                fontFamily
                              }}
                            >
                              Account Name
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Enter account name"
                              value={account.accountName}
                              onChange={(e) =>
                                handleAccountChange(index, "accountName", e.target.value)
                              }
                              inputProps={{ style: { fontFamily } }}
                              variant="outlined"
                            />
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                          <IconButton
                            onClick={() => handleRemoveAccount(index)}
                            disabled={accounts.length === 1}
                            sx={{
                              color: theme.palette.error.main,
                              "&:hover": {
                                backgroundColor: theme.palette.error.light + "20",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          color="primary"
          sx={{ borderRadius: 1, fontWeight: 600, fontFamily, textTransform: 'none', px: 3, minWidth: 120 }}
        >
          {isEdit ? "Save" : "Add Fund"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FundsDialog;